/* eslint-disable react/jsx-no-useless-fragment */
import {
  InfoOutlined,
  StackedLineChartRounded,
  WbSunny,
} from '@mui/icons-material';
import AnimationIcon from '@mui/icons-material/Animation';
import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import {
  Box,
  CircularProgress,
  Fade,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Switch,
  Typography,
} from '@mui/material';
import LayerToggle from '../../common-components/layer-toggle/layer-toggle';
import MUISlider from '../../common-components/slider/SliderMUI';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useAccessControl from '../../hooks/access-control/useAccessControl';
import MapLayer, {
  MapGroupLayer,
} from '../../map/map-layer-manager/map-layer.enum';
import MapHelpers from '../../map/map.utils';
import {
  setShippingTrafficV2Checked,
  setShippingTrafficV2Opacity,
  setToolsPanelViewState,
  ToolsPanelViewState,
} from './tools-panel.slice';

function ToolsMenu() {
  const dispatch = useAppDispatch();
  const {
    rangeRings,
    measuringTool,
    shippingTrafficV2Checked,
    shippingTrafficV2Opacity,
    shippingTrafficLayerExists,
  } = useAppSelector((state) => state.toolsPanel);
  const { vessels } = useAppSelector((state) => state.nearbyVessels);

  const { results } = useAppSelector((state) => state.weather);
  const {
    canAccessNearbyVessels,
    canAccessWeather,
    canAccessShippingTrafficV2,
  } = useAccessControl();

  const handleShippingTrafficV2OpacityChange = (newOpacity: number) => {
    MapHelpers.setLayerOpacity(MapLayer.AGOL_SHIPPING_TRAFFIC, newOpacity);
    dispatch(setShippingTrafficV2Opacity(newOpacity));
  };

  const handleShippingTrafficV2Checked = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!canAccessShippingTrafficV2) return;
    const exists = MapHelpers.layerExists(MapLayer.AGOL_SHIPPING_TRAFFIC);
    if (!exists) return;
    MapHelpers.setLayoutProperty(
      MapLayer.AGOL_SHIPPING_TRAFFIC,
      'visibility',
      event.target.checked ? 'visible' : 'none'
    );
    dispatch(setShippingTrafficV2Checked(event.target.checked));
  };

  const shippingTrafficInfoText = `Shipping traffic data is generated weekly based on traffic patterns monitored via vessels AIS data over the last 7 days. AIS data for all vessels is down sampled to an hourly resolution, with vessels transmitting invalid position data or with implausible average speeds removed to reduce noise in the results.

  This data is then used to construct vectors for vessels typical traffic patterns over the last 7 days, which can then be overlaid to show the traffic patterns that are currently in most active use.
  `;
  return (
    <List data-testid="tools-menu">
      <ListItem
        disablePadding
        secondaryAction={
          <>
            {measuringTool.measurements.length > 0 && (
              <LayerToggle
                layerGroups={[MapGroupLayer.MEASUREMENTS]}
                layers={[]}
              />
            )}
          </>
        }
      >
        <ListItemButton
          onClick={() =>
            dispatch(setToolsPanelViewState(ToolsPanelViewState.MEASURING_TOOL))
          }
        >
          <ListItemIcon
            sx={{
              color: 'secondary.main',
            }}
          >
            <SquareFootIcon />
          </ListItemIcon>
          Measuring Tool
        </ListItemButton>
      </ListItem>
      {canAccessNearbyVessels && (
        <ListItem
          disablePadding
          secondaryAction={
            <>
              {vessels !== null && (
                <LayerToggle
                  layerGroups={[]}
                  layers={['nearby-vessels', 'nearby-vessels-area']}
                />
              )}
            </>
          }
        >
          <ListItemButton
            onClick={() =>
              dispatch(
                setToolsPanelViewState(ToolsPanelViewState.NEARBY_VESSELS)
              )
            }
          >
            <ListItemIcon
              sx={{
                color: 'secondary.main',
              }}
            >
              <ShareLocationIcon />
            </ListItemIcon>
            Nearby Vessels
          </ListItemButton>
        </ListItem>
      )}
      <ListItem
        disablePadding
        secondaryAction={
          <>
            {rangeRings.points.length > 0 && (
              <LayerToggle
                layerGroups={[MapGroupLayer.RANGE_RINGS]}
                layers={[]}
              />
            )}
          </>
        }
      >
        <ListItemButton
          onClick={() =>
            dispatch(setToolsPanelViewState(ToolsPanelViewState.RANGE_RINGS))
          }
        >
          <ListItemIcon
            sx={{
              color: 'secondary.main',
            }}
          >
            <AnimationIcon />
          </ListItemIcon>
          Range Rings
        </ListItemButton>
      </ListItem>
      {canAccessWeather && (
        <ListItem
          disablePadding
          secondaryAction={
            <>
              {results !== null && (
                <LayerToggle
                  layerGroups={[MapGroupLayer.WEATHER]}
                  layers={[]}
                />
              )}
            </>
          }
        >
          <ListItemButton
            onClick={() =>
              dispatch(setToolsPanelViewState(ToolsPanelViewState.WEATHER))
            }
          >
            <ListItemIcon
              sx={{
                color: 'secondary.main',
              }}
            >
              <WbSunny />
            </ListItemIcon>
            Weather
          </ListItemButton>
        </ListItem>
      )}
      {canAccessShippingTrafficV2 && (
        <ListItem
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            paddingRight: '34px',
          }}
          role={undefined}
          dense
        >
          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                width: '100%',
              }}
            >
              <ListItemIcon
                sx={{
                  color: 'secondary.main',
                }}
              >
                <StackedLineChartRounded />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '1rem',
                      lineHeight: '1.5',
                      whiteSpace: 'nowrap',
                      width: 'auto',
                      flexGrow: 1,
                    }}
                    noWrap
                  >
                    Shipping Lanes
                  </Typography>
                }
                sx={{ width: 'auto', minWidth: 0, flexGrow: 1 }}
              />
              <ListItemIcon>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 72,
                    flexWrap: 'wrap',
                    flexShrink: 0,
                  }}
                >
                  <IconButton
                    disableRipple
                    sx={{ '&:hover': { backgroundColor: 'transparent' } }}
                  >
                    <InfoOutlined titleAccess={shippingTrafficInfoText} />
                  </IconButton>
                  {!shippingTrafficLayerExists ? (
                    <CircularProgress
                      size={20}
                      sx={{ color: 'secondary.main' }}
                    />
                  ) : null}
                  <Switch
                    data-testid="shipping-traffic-map-switch"
                    checked={shippingTrafficV2Checked}
                    onChange={handleShippingTrafficV2Checked}
                    disabled={!shippingTrafficLayerExists}
                  />
                </Box>
              </ListItemIcon>
            </Box>
            {shippingTrafficV2Checked && (
              <Fade in={shippingTrafficV2Checked}>
                <Box mx="0.5rem">
                  <Typography variant="caption">
                    Opacity: {shippingTrafficV2Opacity}%
                  </Typography>
                  <MUISlider
                    sx={{ width: '95%' }}
                    name="shippingTrafficV2Opacity"
                    id="shippingTrafficV2Opacity-slider"
                    value={shippingTrafficV2Opacity}
                    onChange={(_event, value) => {
                      handleShippingTrafficV2OpacityChange(value as number);
                    }}
                    disabled={!shippingTrafficV2Checked}
                    aria-label="Shipping Traffic Opacity"
                    valueLabelDisplay="off"
                    min={1}
                    max={100}
                  />
                </Box>
              </Fade>
            )}
          </Box>
        </ListItem>
      )}
    </List>
  );
}

export default ToolsMenu;
